/**
 * KTS Video Upload — Uploads recorded video to Supabase Storage.
 *
 * Files stored in `prospects-media/{user_id}/kts/{session_id}.webm`
 * Returns the relative path (for prospect_updates.media_url) which the
 * Media tab resolves via createSignedUrl.
 */

import { supabase } from '@/supabase/client';

const BUCKET = 'prospects-media';
const MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

/**
 * Upload KTS video and return the relative storage path.
 * The Media tab uses this path with createSignedUrl to display the video.
 */
export async function uploadKtsVideo(
  blob: Blob,
  userId: string,
  sessionId: string,
): Promise<string> {
  if (blob.size > MAX_SIZE_BYTES) {
    throw new Error(`Video too large: ${(blob.size / 1024 / 1024).toFixed(1)}MB (max 100MB)`);
  }

  const ext = blob.type.includes('webm') ? 'webm' : 'mp4';
  const path = `${userId}/kts/${sessionId}.${ext}`;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, {
      contentType: blob.type,
      upsert: true,
    });

  if (error) {
    console.error('[kts-upload] Upload failed', error.message);
    throw new Error(`Upload failed: ${error.message}`);
  }

  // Return relative path — Media tab uses createSignedUrl(path) to display
  return data.path;
}
